import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LOGO_URL } from "../mock/mock";
import { Menu, X, ChevronDown } from "lucide-react";

const OFFER_ITEMS = [
  { label: "Our Guides", path: "/what-we-offer/guides", description: "Expert-written MMI resources" },
  { label: "Maya", path: "/what-we-offer/maya", description: "AI-powered MMI practice" },
  { label: "University-Specific Mock MMI", path: "/what-we-offer/mock-mmi", description: "Live 12-Station Mock Interview" },
];

const OfferDropdown = ({ pathname }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const active =
    pathname.startsWith("/what-we-offer") ||
    pathname.startsWith("/maya") ||
    pathname === "/resources";

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div
      className="relative"
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
      onFocus={cancelClose}
      onBlur={scheduleClose}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`text-[15px] font-medium transition-colors inline-flex items-center gap-1 ${active ? "text-[#1d4ed8]" : "text-slate-800 hover:text-[#1d4ed8]"}`}
        data-testid="nav-what-we-offer-trigger"
      >
        <span className="relative inline-block">
          What We Offer
          {active && (
            <span className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-8 h-[2px] bg-[#1d4ed8] rounded-full" />
          )}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Hover-bridge (prevents accidental close between trigger and menu) */}
      <div className="absolute left-0 right-0 h-3 top-full" aria-hidden />

      <div
        role="menu"
        className={`absolute left-1/2 -translate-x-1/2 top-[calc(100%+10px)] w-[320px] rounded-2xl bg-white border border-slate-100 shadow-[0_30px_70px_-25px_rgba(29,78,216,0.35)] p-2 transition-all duration-200 origin-top ${open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"}`}
        data-testid="nav-what-we-offer-menu"
      >
        {OFFER_ITEMS.map((it) => (
          <Link
            key={it.path}
            to={it.path}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex flex-col gap-0.5 rounded-xl px-4 py-3 hover:bg-[#eef4ff] transition"
            data-testid={`nav-offer-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="text-[14.5px] font-semibold text-slate-900">{it.label}</span>
            <span className="text-[12.5px] text-slate-500">{it.description}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const LINKS = [
  { label: "Home", path: "/" },
  { label: "Our Story", path: "/about" },
  // "What We Offer" is a dropdown — not a link.
  { label: "Pricing", path: "/pricing" },
];

const Navbar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Medily" className="h-8 md:h-9" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.slice(0, 2).map((l) => {
            const active = pathname === l.path || (l.path === "/" && pathname === "/");
            return (
              <Link
                key={l.path}
                to={l.path}
                className={`text-[15px] font-medium transition-colors ${active ? "text-[#1d4ed8]" : "text-slate-800 hover:text-[#1d4ed8]"}`}
              >
                <span className="relative inline-block">
                  {l.label}
                  {active && (
                    <span className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-8 h-[2px] bg-[#1d4ed8] rounded-full" />
                  )}
                </span>
              </Link>
            );
          })}
          <OfferDropdown pathname={pathname} />
          {LINKS.slice(2).map((l) => {
            const active = pathname === l.path;
            return (
              <Link
                key={l.path}
                to={l.path}
                className={`text-[15px] font-medium transition-colors ${active ? "text-[#1d4ed8]" : "text-slate-800 hover:text-[#1d4ed8]"}`}
              >
                <span className="relative inline-block">
                  {l.label}
                  {active && (
                    <span className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-8 h-[2px] bg-[#1d4ed8] rounded-full" />
                  )}
                </span>
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => navigate("/login")}
            className="px-5 py-2.5 rounded-full text-[15px] font-medium text-[#1d4ed8] border border-[#1d4ed8]/40 hover:bg-[#eef2ff] transition"
            data-testid="nav-login-btn"
          >
            Log in
          </button>
          <button
            onClick={() => navigate("/pricing")}
            className="px-5 py-2.5 rounded-full text-[15px] font-medium text-white bg-[#1d4ed8] hover:bg-[#1e40af] transition shadow-sm"
            data-testid="nav-get-started-btn"
          >
            Get Started
          </button>
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-100 bg-white px-6 py-4 space-y-3">
          <Link to="/" onClick={() => setOpen(false)} className="block text-slate-800 font-medium">Home</Link>
          <Link to="/about" onClick={() => setOpen(false)} className="block text-slate-800 font-medium">Our Story</Link>
          <div className="pt-2">
            <p className="text-[12px] uppercase tracking-[0.14em] text-slate-400 font-semibold">What We Offer</p>
            <div className="mt-2 space-y-2 pl-2">
              {OFFER_ITEMS.map((it) => (
                <Link key={it.path} to={it.path} onClick={() => setOpen(false)} className="block text-slate-800 font-medium">
                  {it.label}
                </Link>
              ))}
            </div>
          </div>
          <Link to="/pricing" onClick={() => setOpen(false)} className="block text-slate-800 font-medium">Pricing</Link>
          <div className="flex gap-3 pt-2">
            <button onClick={() => navigate("/login")} className="px-4 py-2 rounded-full text-[#1d4ed8] border border-[#1d4ed8]/40">Log in</button>
            <button onClick={() => navigate("/pricing")} className="px-4 py-2 rounded-full text-white bg-[#1d4ed8]">Get Started</button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
